import type { ReactNode } from 'react';
import { createContext, useContext, useState } from 'react';

interface AlertItem {
  id: string;
  message: string;
  variant: 'success' | 'error';
}

interface AlertContextProps {
  setSuccess: (msg: string) => void;
  setError: (msg: string) => void;
  alerts: AlertItem[];
  onClose: (id: string) => void;
  clearAlerts: () => void;
}

const AlertContext = createContext<AlertContextProps>({
  setError: () => {},
  setSuccess: () => {},
  alerts: [],
  onClose: () => {},
  clearAlerts: () => {},
});

function AlertProvider(props: { children: ReactNode }) {
  const [alerts, setAlerts] = useState<AlertItem[]>([]);

  const addAlert = (message: string, variant: 'success' | 'error') => {
    const id = Date.now().toString();
    setAlerts((prev) => [...prev, { id, message, variant }]);
  };

  const removeAlert = (id: string) => {
    setAlerts((prev) => prev.filter((alert) => alert.id !== id));
  };

  return (
    <AlertContext.Provider
      value={{
        setSuccess: (m) => addAlert(m, 'success'),
        setError: (m) => addAlert(m, 'error'),
        alerts,
        onClose: removeAlert,
        clearAlerts: () => setAlerts([]),
      }}
    >
      {props.children}
    </AlertContext.Provider>
  );
}

function useAlert() {
  return useContext(AlertContext);
}

export { AlertProvider, useAlert };
